import type { TaxonomyAttribute } from 'src/redux/productsQuery/types/ProductsQueryState';
import { TAXONOMY_SKIP_SEGMENT } from './taxonomyPath';

export interface UrlFacet {
  alias: string;
  values: string[];
}

const FILTER_PATH_SEPARATOR = '/-/';

const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const splitAndDecodeCommaSeparated = (segment: string): string[] =>
  segment.split(',').map(decodeSegment);

const stripQueryAndHash = (asPath: string): string => asPath.split('?')[0].split('#')[0];

export const parseUrlFacets = (
  asPath: string,
  taxonomyAttributes: TaxonomyAttribute[],
): UrlFacet[] => {
  const pathOnly = stripQueryAndHash(asPath);
  const separatorIndex = pathOnly.indexOf(FILTER_PATH_SEPARATOR);
  const basePath = separatorIndex === -1 ? pathOnly : pathOnly.slice(0, separatorIndex);
  const filterPath =
    separatorIndex === -1 ? '' : pathOnly.slice(separatorIndex + FILTER_PATH_SEPARATOR.length);

  const facets: UrlFacet[] = [];

  if (taxonomyAttributes.length > 0) {
    const baseSegments = basePath.split('/').filter((segment) => segment.length > 0);
    // First segment is the route literal (e.g. `shop`); always discarded.
    const taxonomySegments = baseSegments.slice(1, 1 + taxonomyAttributes.length);

    taxonomySegments.forEach((segment, index) => {
      if (segment.length === 0 || segment === TAXONOMY_SKIP_SEGMENT) {
        return;
      }
      facets.push({
        alias: taxonomyAttributes[index].alias,
        values: splitAndDecodeCommaSeparated(segment),
      });
    });
  }

  if (filterPath.length > 0) {
    const filterSegments = filterPath.split('/');
    const facetsByAlias = new Map<string, UrlFacet>();
    facets.forEach((facet) => facetsByAlias.set(facet.alias, facet));

    for (let i = 0; i + 1 < filterSegments.length; i += 2) {
      const alias = filterSegments[i];
      const rawValue = filterSegments[i + 1];

      if (!alias || rawValue === undefined) {
        continue;
      }

      const value = decodeSegment(rawValue);
      const existing = facetsByAlias.get(alias);

      if (existing) {
        existing.values.push(value);
      } else {
        const facet: UrlFacet = { alias, values: [value] };
        facetsByAlias.set(alias, facet);
        facets.push(facet);
      }
    }
  }

  return facets;
};
